import { generateWhispSignalImage } from './whisp-generator'

export interface WispCacheI {
  load(data: string, color: SignalColor, pixelPerRow: number): ImageData | undefined;
  set(data: string, color: SignalColor, pixelPerRow: number, imageData: ImageData): void;
}

function makeCacheKey(data: string, color: SignalColor, pixelPerRow: number): string {
  return `${data}_${color.join('')}_${pixelPerRow}`;
}

export class WispInMemoryCache implements WispCacheI {
  private cache: Map<string, ImageData> = new Map();
  private cacheFifo: string[] = [];

  constructor(private maxCacheSize: number) {}

  load(data: string, color: SignalColor, pixelPerRow: number): ImageData | undefined {
    const key = makeCacheKey(data, color, pixelPerRow);
    return this.cache.get(key);
  }

  set(data: string, color: SignalColor, pixelPerRow: number, imageData: ImageData): void {
    const key = makeCacheKey(data, color, pixelPerRow);
    this.cacheFifo.push(key);
    if (this.cacheFifo.length > this.maxCacheSize) {
      this.cache.delete(this.cacheFifo.shift() as string);
    }
    this.cache.set(key, imageData);
  }
}

export type SignalColor = [R: number, G: number, B: number];

const globalCache = new WispInMemoryCache(10);

export class WispSignal {
  private image: ImageData | null = null;
  private height: number | null = null;
  private width: number | null = null;

  constructor(
    private canvas: HTMLCanvasElement,
    private data: string,
    private color: SignalColor,
    private pixelPerRow: number = 8,
    private isVertical: boolean = false,
    private cache: WispCacheI = globalCache,
  ) {
    this.canvas = canvas;
    this.data = data;
    this.color = color;
    this.pixelPerRow = pixelPerRow;
    this.cache = cache;

    this.validateInputs();
    this.generateAndRenderSignal();
    this.startResizeListener();
  }

  private startResizeListener(): void {
    window.addEventListener('resize', () => this.resizeSignal())
  }

  private validateInputs(): void {
    if (!this.pixelPerRow) this.pixelPerRow = 8;
    if (this.pixelPerRow < 4 || this.pixelPerRow > 16) {
      throw new Error(
        "Invalid pixelPerRow value. It should be between 4 and 16."
      );
    }
    if (this.data.length > 500) {
      throw new Error(
        "Data length exceeds the maximum limit of 500 characters."
      );
    }
  }

  private generateAndRenderSignal() {
    // Check image cache and generate new image if no image is found.
    const cachedImage = this.cache.load(this.data, this.color, this.pixelPerRow);
    if (!cachedImage) {
      const image = generateWhispSignalImage(this.data, this.color, this.pixelPerRow);
      this.cache.set(this.data, this.color, this.pixelPerRow, image);
      this.image = image;
    } else {
      this.image = cachedImage;
    }

    // Handle canvas creation
    if (this.isVertical) {
      this.canvas.width = this.image.height;
      this.canvas.height = this.image.width;
      this.height = this.image.width;
      this.width = this.image.height;

      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = this.image.width;
      tempCanvas.height = this.image.height;
      tempCanvas.getContext('2d')?.putImageData(this.image, 0, 0);

      const context = this.canvas.getContext('2d');
      context?.translate(this.canvas.width/2,this.canvas.height/2);
      context?.rotate((90 * Math.PI) / 180);
      context?.drawImage(tempCanvas, -this.image.width/2, -this.image.height/2);
    } else {
      this.canvas.width = this.image.width;
      this.canvas.height = this.image.height;
      this.height = this.image.height;
      this.width = this.image.width;
      this.canvas.getContext('2d')?.putImageData(this.image, 0, 0);
    }

    this.resizeSignal();
  }

  private resizeSignal(): void {
    if (!this.height || !this.width) return;
    const pixelRatio = window.devicePixelRatio;
    const width = (this.width / pixelRatio);
    const height = (this.height / pixelRatio);
    this.canvas.style.width = width + 'px';
    this.canvas.style.height = height + 'px';
  }

  public updateSignal(props: {data?: string, color?: SignalColor, pixelPerRow?: number}): void {
    const { data, color, pixelPerRow } = props;
    if (data) this.data = data;
    if (color) this.color = color;
    if (pixelPerRow) this.pixelPerRow = pixelPerRow;

    this.validateInputs();
    this.generateAndRenderSignal();
  }

  public destroy(): void {
    window.removeEventListener('resize', () => this.resizeSignal());
    this.canvas.remove();
  }
}
